import {
  Directive,
  Input,
  HostListener,
  ComponentFactoryResolver,
  ElementRef,
  Injector,
  ComponentRef,
  ApplicationRef,
  EmbeddedViewRef,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy,
  TemplateRef,
  Renderer2,
  AfterViewInit,
  Inject,
  PLATFORM_ID,
  OnChanges,
} from '@angular/core';
import { isPlatformServer } from '@angular/common';

import { fromEvent, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { stopPropagation, unsubscribe } from '@fcom/utils';

import { TooltipComponent } from '../component/tooltip.component';
import { TooltipData, TooltipOptions } from '../tooltip.interface';
import { TooltipService } from '../service/tooltip.service';

@Directive({
  selector: '[fcomTooltip]',
})
export class TooltipDirective implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  private cid = uuid();
  private _options: TooltipOptions;

  @Input()
  set options(value: TooltipOptions) {
    this._options = value;
    if (value && value.tooltipID) {
      this.cid = value.tooltipID;
    }
  }
  get options(): TooltipOptions {
    return {
      ...this._options,
      tooltipID: this.cid,
    };
  }

  @Input()
  content: TemplateRef<ElementRef>;

  @Input()
  closeText: string;

  @Input()
  ariaCloseText: string;

  @Output()
  closeClick: EventEmitter<boolean> = new EventEmitter<boolean>();

  private componentRef: ComponentRef<any>;
  private isTooltipDestroyed = true;
  private onContent = false;
  private subscriptions = new Subscription();

  @HostListener('click', ['$event'])
  onClick(event: Event): void {
    stopPropagation(event);
    if (this.options && this.options.enableOnClick) {
      this.toggleTooltip();
    }
  }

  @HostListener('focusin')
  @HostListener('mouseenter')
  onMouseEnter(): void {
    if (this.options && this.options.enableOnHover && this.isTooltipDestroyed) {
      this.createTooltip();
    }
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  onMouseLeave(): void {
    if (this.options && this.options.enableOnHover && !this.isTooltipDestroyed) {
      setTimeout(() => {
        if (!this.onContent) {
          this.destroyTooltip();
        }
      }, 10);
    }
  }

  @HostListener('document:keydown.escape')
  onKeydownHandler(): void {
    this.tryDestroyTooltip();
  }

  constructor(
    private readonly triggerRef: ElementRef,
    private readonly componentFactoryResolver: ComponentFactoryResolver,
    private readonly injector: Injector,
    private readonly appRef: ApplicationRef,
    private readonly renderer: Renderer2,
    private readonly tooltipService: TooltipService,
    @Inject(PLATFORM_ID) private platform: object
  ) {}

  ngOnDestroy(): void {
    this.destroyTooltip();
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  ngOnInit(): void {
    if (!this.options || (!this.options.enableOnClick && !this.options.enableOnHover && !this.options.openByDefault)) {
      this.options = Object.assign({}, this.options, { enableOnClick: true });
    }
    if (this.options && this.options.enableOnClick) {
      this.renderer.setAttribute(this.triggerRef.nativeElement, 'aria-expanded', 'false');
      this.renderer.setAttribute(this.triggerRef.nativeElement, 'aria-controls', this.cid);
    } else if (this.options && this.options.enableOnHover) {
      this.renderer.setAttribute(this.triggerRef.nativeElement, 'aria-describedby', this.cid);
    }
  }

  ngAfterViewInit(): void {
    if (this.options.openByDefault) {
      this.createTooltip();
    }
  }

  updateTooltipData(): void {
    if (!this.componentRef?.instance) {
      return;
    }

    this.componentRef.instance.tooltipData = {
      content: this.content,
      closeText: this.closeText,
      ariaCloseText: this.ariaCloseText,
    } as TooltipData;

    this.componentRef.instance.tooltipOptions = this.options;
    this.subscriptions.add(
      this.componentRef.instance.closeClick.pipe(take(1)).subscribe((closeClick: boolean) => {
        this.closeClick.emit(closeClick);
        this.destroyTooltip();
      })
    );
  }

  private insertAfter(newNode, referenceNode): void {
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling);
  }

  private appendComponentToElement(component: any): void {
    this.componentRef = this.componentFactoryResolver.resolveComponentFactory(component).create(this.injector);
    this.componentRef.instance.mouseOnContent.subscribe((onContent: boolean) => {
      this.onContent = onContent;
    });
    this.componentRef.instance.triggerRef = this.triggerRef;
    this.updateTooltipData();
    this.appRef.attachView(this.componentRef.hostView);
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;

    if (this.isTooltipDestroyed) {
      if (this.options && this.options.enableOnHover) {
        domElem.setAttribute('aria-hidden', 'true');
      }
      this.insertAfter(domElem, this.triggerRef.nativeElement);
    }
  }

  ngOnChanges(): void {
    this.updateTooltipData();

    if (this.componentRef) {
      this.componentRef.instance.changeDetectorRef.detectChanges();
    }
  }

  createTooltip(): void {
    this.tooltipService.open(this);
    if (this.options && this.options.enableOnClick) {
      this.renderer.setAttribute(this.triggerRef.nativeElement, 'aria-expanded', 'true');
      this.triggerRef.nativeElement.classList.add('tooltip-open');
    }
    this.appendComponentToElement(TooltipComponent);
    this.addScrollListener();
    this.isTooltipDestroyed = false;
  }

  addScrollListener(): void {
    if (this.options.closeOnScroll && !isPlatformServer(this.platform)) {
      this.subscriptions.add(
        fromEvent(window, 'scroll')
          .pipe(
            filter(() => {
              return !this.isTooltipDestroyed;
            }),
            take(1)
          )
          .subscribe(() => this.tryDestroyTooltip())
      );
    }
  }

  toggleTooltip(): void {
    if (!this.componentRef || this.isTooltipDestroyed) {
      this.createTooltip();
    } else if (!this.isTooltipDestroyed) {
      this.destroyTooltip(true);
    }
  }

  private tryDestroyTooltip(): void {
    if (!this.isTooltipDestroyed) {
      this.destroyTooltip();
    }
  }

  destroyTooltip(shouldFocusOnHost = false): void {
    if (this.isTooltipDestroyed) {
      return;
    }

    if (this.options && this.options.enableOnClick) {
      if (shouldFocusOnHost) {
        this.triggerRef.nativeElement.focus();
      }
      this.renderer.setAttribute(this.triggerRef.nativeElement, 'aria-expanded', 'false');
      this.triggerRef.nativeElement.classList.remove('tooltip-open');
    }
    // remove it from the DOM
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<unknown>).rootNodes[0] as HTMLElement;
    if (!document.body.contains(domElem)) {
      return;
    }
    this.triggerRef.nativeElement.parentNode.removeChild(domElem);

    this.appRef.detachView(this.componentRef.hostView);
    this.componentRef.destroy();
    this.tooltipService.close();
    this.isTooltipDestroyed = true;
  }
}
